import Header from "../../components/user/Header";
import React, { useEffect, useRef, useState, useMemo } from "react";
import baseball from "../../images/Baseball.jpg";
import football from "../../images/Football2.jpg";
import hockey from "../../images/hockey.jpg";
import logo from "../../images/logo.png";
import axios from "axios";
import Cookies from "js-cookie";
import Loader from "../../components/Loader";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Footer from "../../components/user/Footer";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const apiKey = process.env.REACT_APP_API_KEY;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function Home() {
  const getUsername = Cookies.get("username");
  const getRole = Cookies.get("user_role");

  const [formValues, setFormValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    jersey_number: "",
    address: "",        // will be auto-filled with EVENT city/location
    height_in: "",
    handedness: "",
    primary_position: "",
    dob: "",
    grad_year: "",
    weight_lb: "",
    dominant_foot: "",
    school_club: "",
    event_name: "",     // will be auto-filled
  });

  const [loading, setLoading] = useState(false);

  // ✅ Athlete flow: sport -> events dropdown -> auto-fill event fields
  const [sport, setSport] = useState(""); // SportCategory
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEventCode, setSelectedEventCode] = useState(""); // dropdown value
  const [selectedEvent, setSelectedEvent] = useState(null);

  // ✅ UI/validation messages
  const [eventError, setEventError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [activeTab, setActiveTab] = useState("certified");

  const [showPayment, setShowPayment] = useState(false);
  const [tempUploadData, setTempUploadData] = useState(null);
  const emailRef = useRef(null);

  const navigate = useNavigate();
  const API_URL = BASE_URL + "/addplayer";
  const EVENTS_API = BASE_URL + "/getevents";

  // ✅ Fetch user details if logged in
  useEffect(() => {
    if (getUsername || getRole) {
      axios
        .get(`${BASE_URL}/getusers`, { headers: { Authorization: apiKey } })
        .then((res) => {
          const user = res.data.find((u) => u.Name === getUsername);
          if (user) {
            setFormValues((prev) => ({
              ...prev,
              first_name: user.FirstName || "",
              last_name: user.LastName || "",
              email: user.Email || "",
              jersey_number: user.JerseyNumber || "",
              address: user.Address || "",
              height_in: user.Height_in || "",
              handedness: user.Handedness || "",
              primary_position: user.Position || "",
              dob: user.DateOfBirth || "",
              grad_year: user.GraduationYear || "",
              weight_lb: user.Weight || "",
              dominant_foot: user.DominantFoot || "",
              school_club: user.SchoolClub || "",
              event_name: user.EventName || "",
            }));
          }
        })
        .catch((err) => console.error("Error fetching user:", err));
    }
  }, [getUsername, getRole]);

  // ✅ Fetch all events
  useEffect(() => {
    axios
      .get(EVENTS_API, { headers: { Authorization: apiKey } })
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching events:", err));
  }, []);

  // ✅ Helper: normalize event fields regardless of backend casing
  const normalizeEvent = (ev) => {
    const eventCode = ev.eventCode ?? ev.EventCode ?? ev.code ?? "";
    const sportCategory = ev.sportCategory ?? ev.SportCategory ?? ev.sport ?? "";
    const eventName = ev.eventName ?? ev.EventName ?? ev.name ?? "";
    const eventDate = ev.eventDate ?? ev.EventDate ?? ev.date ?? "";
    const cityLocation = ev.cityLocation ?? ev.CityLocation ?? ev.city ?? ev.location ?? "";
    return { raw: ev, eventCode, sportCategory, eventName, eventDate, cityLocation };
  };

  // ✅ Recompute filtered events when sport/events change
  useEffect(() => {
    setEventError("");
    setSelectedEventCode("");
    setSelectedEvent(null);

    if (!sport) {
      setFilteredEvents([]);
      // Clear auto-filled event fields
      setFormValues((prev) => ({ ...prev, event_name: "", address: "" }));
      return;
    }

    const nextFiltered = events
      .map(normalizeEvent)
      .filter((ev) => (ev.sportCategory || "").toLowerCase() === sport.toLowerCase());

    setFilteredEvents(nextFiltered);

    // Clear auto-filled fields if no events
    if (nextFiltered.length === 0) {
      setFormValues((prev) => ({ ...prev, event_name: "", address: "" }));
    }
  }, [sport, events]);

  // ✅ When event selected -> auto-fill Event Code (hidden), Event Date, City/Location, Event Name
  const handleEventSelect = (code) => {
    setEventError("");
    setSelectedEventCode(code);

    const ev = filteredEvents.find((x) => String(x.eventCode) === String(code));
    setSelectedEvent(ev || null);

    if (ev) {
      setFormValues((prev) => ({
        ...prev,
        event_name: ev.eventName || "",
        address: ev.cityLocation || "", // per requirement: City / Location auto-filled
      }));
    } else {
      setFormValues((prev) => ({ ...prev, event_name: "", address: "" }));
    }
  };

  // ✅ Handle input changes
  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const noEventsForSport = useMemo(() => !!sport && filteredEvents.length === 0, [sport, filteredEvents]);

  // ✅ Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setEventError("");
    setEmailError("");
    setLoading(true);

    const formData = new FormData(e.target);
    const sportCategory = sport || formData.get("sportCategory"); // sport state is source of truth

    try {
      // ✅ Validation: Athlete must choose a valid event (no typing event codes)
      if (!sportCategory) {
        setEventError("Please select a sport.");
        setLoading(false);
        return;
      }

      if (noEventsForSport) {
        setEventError("No events available. Please contact administrator.");
        setLoading(false);
        return;
      }

      if (!selectedEvent || !selectedEvent.eventCode) {
        setEventError("Please select a valid event.");
        setLoading(false);
        return;
      }

      // 🔹 Build player payload WITHOUT files for certified users
      const payload = {
        SportCategory: sportCategory || null,

        // ✅ Event fields auto-handled by system
        EventCode: selectedEvent.eventCode || null,
        EventName: selectedEvent.eventName || null,
        EventDate: selectedEvent.eventDate || null,
        CityLocation: selectedEvent.cityLocation || null,

        PlayerName: `${formData.get("first_name") || ""} ${formData.get("last_name") || ""}`.trim(),
        Email: formData.get("email") || null,
        JerseyNumber: formData.get("jersey_number") || null,
        Notes: formData.get("notes") || null,

        positions: formData.getAll("positions") || [],
        DocumentFile: null,
        VideoFile: null,
        stats: {},

        // store original files to upload after payment
        DocumentFileFileInput: e.target.pdf?.files[0] || null,
        VideoFileFileInput: e.target.video?.files[0] || null,
      };

      // ✅ Sport-specific stats
      switch (sportCategory) {
        case "Basketball":
          Object.assign(payload, {
            dominant_hand: formData.get("dominant_hand") || null,
            height_in: formData.get("height_in") || null,
            wingspan_in: formData.get("wingspan_in") || null,
            vertical_jump_in: formData.get("vertical_jump_in") || null,
            forty_yard_time_sec: formData.get("forty_yard_time_sec") || null,
            position: formData.get("basketball_position") || null,
          });
          break;
        case "Baseball":
          Object.assign(payload, {
            bats: formData.get("bats") || null,
            throws: formData.get("throws") || null,
            exit_velo_max_mph: formData.get("exit_velo_max_mph") || null,
            pop_time_sec: formData.get("pop_time_sec") || null,
            sixty_yard_time_sec: formData.get("sixty_yard_time_sec") || null,
            positions: formData.getAll("positions") || [],
          });
          break;
        case "Football":
          Object.assign(payload, {
            handedness: formData.get("handedness") || null,
            forty_yard_time_sec: formData.get("forty_yard_time_sec") || null,
            bench_reps_225: formData.get("bench_reps_225") || null,
            vertical_jump_in: formData.get("vertical_jump_in") || null,
            positions: formData.getAll("positions") || [],
          });
          break;
        case "Hockey":
          Object.assign(payload, {
            shoots: formData.get("shoots") || null,
            position: formData.get("position") || null,
          });
          break;
        case "Soccer":
          Object.assign(payload, {
            dominant_foot: formData.get("dominant_foot") || null,
            forty_meter_time_sec: formData.get("forty_meter_time_sec") || null,
            positions: formData.getAll("positions") || [],
          });
          break;
        default:
          break;
      }

      // 🔹 Certified → show payment first
      if (activeTab === "certified") {
        setTempUploadData(payload);
        setShowPayment(true);
        setLoading(false);
        return;
      }

      // 🔹 Community → direct submit WITH files
      if (activeTab === "community") {
        const readFileAsBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

        const filesPayload = {};
        const pdfFile = e.target.pdf?.files[0];
        const videoFile = e.target.video?.files[0];

        if (pdfFile) filesPayload.DocumentFile = await readFileAsBase64(pdfFile);
        if (videoFile) filesPayload.VideoFile = await readFileAsBase64(videoFile);

        if (Object.keys(filesPayload).length > 0) {
          const uploadRes = await axios.post(`${BASE_URL}/upload-files-json`, filesPayload, {
            headers: { Authorization: apiKey, "Content-Type": "application/json" },
          });
          payload.DocumentFile = uploadRes.data.files?.DocumentFile || null;
          payload.VideoFile = uploadRes.data.files?.VideoFile || null;
        }

        const res = await axios.post(API_URL, payload, {
          headers: { Authorization: apiKey, "Content-Type": "application/json" },
        });

        if (res.data?.message === "Duplicate avoided. EventCode updated.") {
          setEmailError("This email has already submitted this form.");
          setLoading(false);
          setTimeout(() => emailRef.current?.focus(), 50);
          return;
        }

        toast.success("Player registered successfully!");
        navigate("/Thankyou", { state: { fromForm: true } });
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Stripe Payment Form Component (unchanged except it uses tempUploadData fields, which are now auto-filled)
  const PaymentForm = ({ onPaymentSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loadingPay, setLoadingPay] = useState(false);
    const [userTier, setUserTier] = useState(null);
    const [discountCode, setDiscountCode] = useState("");
    const [enteredCode, setEnteredCode] = useState("");
    const [discountPercent, setDiscountPercent] = useState(0);
    const [discountMessage, setDiscountMessage] = useState("");
    const [baseAmount] = useState(100); // $1 base
    const [finalAmount, setFinalAmount] = useState(100);

    useEffect(() => {
      if (getUsername) {
        axios
          .get(`${BASE_URL}/getusers`, { headers: { Authorization: apiKey } })
          .then((res) => {
            const user = res.data.find((u) => u.Name === getUsername);
            if (user && user.DiscountCode?.code) {
              const expiry = user.DiscountCode.expiresAt?._seconds * 1000;
              if (expiry > Date.now()) {
                setUserTier(user.Tier);
                setDiscountCode(user.DiscountCode.code);
              }
            }
          })
          .catch((err) => console.error("Error fetching discount info:", err));
      }
    }, [getUsername]);

    const discountMap = {
      Silver: { code: "SILVER5", percent: 5 },
      Gold: { code: "GOLD10", percent: 10 },
      Platinum: { code: "PLAT20", percent: 20 },
    };

    const applyDiscount = () => {
      if (!userTier || !discountCode) {
        setDiscountMessage("No discount code available for your account.");
        return;
      }

      const valid = discountMap[userTier];
      if (enteredCode.trim().toUpperCase() === valid.code) {
        setDiscountPercent(valid.percent);
        const newAmount = baseAmount - (baseAmount * valid.percent) / 100;
        setFinalAmount(newAmount);
        setDiscountMessage(`${valid.percent}% discount applied!`);
      } else {
        setDiscountMessage("Invalid discount code.");
      }
    };

    const handlePayment = async (e) => {
      e.preventDefault();
      setLoadingPay(true);

      try {
        const { data: paymentData } = await axios.post(`${BASE_URL}/create-payment-intent`, {
          amount: finalAmount,
          email: tempUploadData.Email,
          athleteName: tempUploadData.PlayerName,
          eventName: tempUploadData.EventName,
          sport: tempUploadData.SportCategory,
        });

        const clientSecret = paymentData.clientSecret;
        const cardElement = elements.getElement(CardElement);

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: tempUploadData.Email,
              name: tempUploadData.PlayerName,
            },
          },
        });

        if (error) {
          toast.error(error.message);
          setLoadingPay(false);
          return;
        }

        const confirmRes = await axios.post(`${BASE_URL}/confirm-payment`, {
          paymentIntentId: paymentIntent.id,
          paymentMethodId: paymentIntent.payment_method,
        });

        if (confirmRes.data.status !== "succeeded") {
          toast.error("Payment not confirmed. Please try again.");
          setLoadingPay(false);
          return;
        }

        toast.success("Payment Successful! Uploading files...");

        const filesPayload = {};
        const readFileAsBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

        const pdfFile = tempUploadData.DocumentFileFileInput;
        const videoFile = tempUploadData.VideoFileFileInput;

        if (pdfFile) filesPayload.DocumentFile = await readFileAsBase64(pdfFile);
        if (videoFile) filesPayload.VideoFile = await readFileAsBase64(videoFile);

        let uploadedFiles = { DocumentFile: null, VideoFile: null };
        if (Object.keys(filesPayload).length > 0) {
          const uploadRes = await axios.post(`${BASE_URL}/upload-files-json`, filesPayload, {
            headers: { Authorization: apiKey, "Content-Type": "application/json" },
          });

          uploadedFiles = {
            DocumentFile: uploadRes.data.files?.DocumentFile || null,
            VideoFile: uploadRes.data.files?.VideoFile || null,
          };
        }

        const playerPayload = {
          ...tempUploadData,
          DocumentFile: uploadedFiles.DocumentFile,
          VideoFile: uploadedFiles.VideoFile,
        };

        const addPlayerRes = await axios.post(API_URL, playerPayload, {
          headers: { Authorization: apiKey, "Content-Type": "application/json" },
        });

        if (addPlayerRes.data?.message === "Duplicate avoided. EventCode updated.") {
          toast.info("This form has already been submitted.");
        } else {
          toast.success("Player registered successfully!");
        }

        navigate("/Thankyou", { state: { fromForm: true } });
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong during payment or file upload!");
      } finally {
        setLoadingPay(false);
      }
    };

    return (
      <form onSubmit={handlePayment}>
        <div style={{ border: "1px solid #ccc", borderRadius: "10px", padding: "12px 20px", background: "#fff" }}>
          <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
        </div>

        {discountCode && (
          <div className="mt-3">
            <label>Discount Code</label>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                value={enteredCode}
                onChange={(e) => setEnteredCode(e.target.value)}
                placeholder={`Enter your code (${discountCode})`}
              />
              <button type="button" onClick={applyDiscount} className="btn btn-primary">
                Apply
              </button>
            </div>
            {discountMessage && <small className="text-success d-block mt-1">{discountMessage}</small>}
          </div>
        )}

        <div className="mt-3">
          <strong>Amount:</strong>{" "}
          {discountPercent > 0 ? (
            <>
              <span style={{ textDecoration: "line-through", color: "gray" }}>
                ${(baseAmount / 100).toFixed(2)}
              </span>{" "}
              <span style={{ color: "green" }}>${(finalAmount / 100).toFixed(2)}</span>
            </>
          ) : (
            <span>${(finalAmount / 100).toFixed(2)}</span>
          )}
        </div>

        <button type="submit" className="btn btn-dark mt-3" disabled={!stripe || loadingPay}>
          {loadingPay ? "Processing..." : `Pay $${(finalAmount / 100).toFixed(2)} & Submit`}
        </button>
      </form>
    );
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Header />
      <ToastContainer />

      {/* ... carousel unchanged ... */}

      {/* 🔹 Tabs for Certified vs Community */}
      <div className="container my-4">
        <ul className="nav nav-tabs justify-content-center">
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "certified" ? "active" : ""}`} onClick={() => setActiveTab("certified")}>
              Certified Athlete
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "community" ? "active" : ""}`} onClick={() => setActiveTab("community")}>
              Community Submission
            </button>
          </li>
        </ul>
      </div>

      <div className="container-fluid" style={{ background: "linear-gradient(to right, #e0f2ff, #ffffff)" }}>
        <h3 style={{ marginBottom: "20px", fontWeight: "bold", textAlign: "center" }}>
          Player Stat Entry Form
        </h3>

        <div className="row align-items-center justify-content-center">
          <div className="col-md-8 p-5">
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <img src={logo} alt="Logo" height="150px" className="d-inline-block align-top me-2" />
              <br />
              <br />
              {!getUsername && (
                <p>
                  Already have an account? Click <Link to="/login">here</Link> to login. Or, click{" "}
                  <Link to="/signup">here</Link> to sign up for a new account.
                </p>
              )}
            </div>

            {!showPayment ? (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Left Column */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <small className="text">First Name*</small>
                      <input
                        type="text"
                        className="form-control"
                        name="first_name"
                        value={formValues.first_name}
                        onChange={handleChange}
                        required
                        placeholder="Enter First Name"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Email*</small>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formValues.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter Email"
                        ref={emailRef}
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                      {emailError && <small style={{ color: "red" }}>{emailError}</small>}
                    </div>

                    <div className="mb-3">
                      <small className="text">Jersey Number*</small>
                      <input
                        type="number"
                        className="form-control"
                        name="jersey_number"
                        value={formValues.jersey_number}
                        onChange={handleChange}
                        required
                        placeholder="Enter Jersey Number"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    {/* ✅ City/Location AUTO-FILLED from event */}
                    <div className="mb-3">
                      <small className="text">City / Location*</small>
                      <input
                        type="text"
                        className="form-control"
                        name="address"
                        value={formValues.address}
                        readOnly
                        required
                        placeholder="Auto-filled after selecting an event"
                        style={{ borderRadius: "10px", padding: "12px 20px", backgroundColor: "#f7f7f7" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Height (in)*</small>
                      <input
                        type="number"
                        className="form-control"
                        name="height_in"
                        required
                        placeholder="Enter Height in Inches"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Handedness*</small>
                      <select className="form-control" name="handedness" required style={{ borderRadius: "10px", padding: "12px 20px" }}>
                        <option value="">Select</option>
                        <option value="Right">Right</option>
                        <option value="Left">Left</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <small className="text">Primary Position*</small>
                      <input
                        type="text"
                        className="form-control"
                        name="primary_position"
                        required
                        placeholder="Enter Primary Position"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    {/* ✅ Event Code is NEVER typed. Hidden input to submit to backend. */}
                    <input type="hidden" name="event_code" value={selectedEvent?.eventCode || ""} />

                    <div className="mb-3">
                      <small className="text">Graduation Year*</small>
                      <input
                        type="month"
                        className="form-control"
                        name="grad_year"
                        value={formValues.grad_year}
                        onChange={handleChange}
                        required
                        placeholder="Enter Graduation Year"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <label>Upload Video</label>
                      <input
                        type="file"
                        accept="video/*"
                        className="form-control"
                        required
                        name="video"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <small className="text">Last Name*</small>
                      <input
                        type="text"
                        className="form-control"
                        name="last_name"
                        value={formValues.last_name}
                        onChange={handleChange}
                        required
                        placeholder="Enter Last Name"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Date of Birth*</small>
                      <input
                        type="date"
                        className="form-control"
                        name="dob"
                        value={formValues.dob}
                        onChange={handleChange}
                        required
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    {/* ✅ Event Date AUTO-FILLED */}
                    <div className="mb-3">
                      <small className="text">Event Date*</small>
                      <input
                        type="date"
                        className="form-control"
                        name="event_date"
                        value={selectedEvent?.eventDate ? String(selectedEvent.eventDate).slice(0, 10) : ""}
                        readOnly
                        required
                        placeholder="Auto-filled after selecting an event"
                        style={{ borderRadius: "10px", padding: "12px 20px", backgroundColor: "#f7f7f7" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Weight (lb)*</small>
                      <input
                        type="number"
                        className="form-control"
                        name="weight_lb"
                        required
                        placeholder="Enter Weight in Pounds"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Dominant Foot*</small>
                      <select className="form-control" name="dominant_foot" required style={{ borderRadius: "10px", padding: "12px 20px" }}>
                        <option value="">Select</option>
                        <option value="Right">Right</option>
                        <option value="Left">Left</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <small className="text">School / Club*</small>
                      <input
                        type="text"
                        className="form-control"
                        name="school_club"
                        value={formValues.school_club}
                        onChange={handleChange}
                        required
                        placeholder="Enter School / Club"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    {/* ✅ Event Name AUTO-FILLED (prevents mismatches) */}
                    <div className="mb-3">
                      <small className="text">Event Name*</small>
                      <input
                        type="text"
                        className="form-control"
                        name="event_name"
                        value={formValues.event_name}
                        readOnly
                        required
                        placeholder="Auto-filled after selecting an event"
                        style={{ borderRadius: "10px", padding: "12px 20px", backgroundColor: "#f7f7f7" }}
                      />
                    </div>

                    <div className="mb-3">
                      <label>Upload PDF</label>
                      <input
                        type="file"
                        accept=".pdf"
                        className="form-control"
                        required
                        name="pdf"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    {/* ✅ Sport selection */}
                    <div className="mb-3">
                      <small className="text">Select Sport*</small>
                      <select
                        className="form-control"
                        name="sportCategory"
                        value={sport}
                        onChange={(e) => setSport(e.target.value)}
                        required
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      >
                        <option value="">Select Sport</option>
                        <option value="Basketball">Basketball</option>
                        <option value="Baseball">Baseball</option>
                        <option value="Football">Football</option>
                        <option value="Soccer">Soccer</option>
                        <option value="Hockey">Hockey</option>
                      </select>
                    </div>

                    {/* ✅ Event dropdown (NO code typing) */}
                    <div className="mb-3">
                      <small className="text">Select Event*</small>
                      <select
                        className="form-control"
                        value={selectedEventCode}
                        onChange={(e) => handleEventSelect(e.target.value)}
                        required
                        disabled={!sport || filteredEvents.length === 0}
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      >
                        <option value="">
                          {!sport
                            ? "Select sport first"
                            : filteredEvents.length === 0
                            ? "No events available"
                            : "Select Event"}
                        </option>

                        {filteredEvents.map((ev) => (
                          <option key={ev.eventCode} value={ev.eventCode}>
                            {ev.eventName || "Event"}{" "}
                            {ev.eventDate ? `• ${String(ev.eventDate).slice(0, 10)}` : ""}{" "}
                            {ev.cityLocation ? `• ${ev.cityLocation}` : ""}
                          </option>
                        ))}
                      </select>

                      {/* ✅ Required message when no events */}
                      {noEventsForSport && (
                        <small style={{ color: "red" }}>
                          No events available. Please contact administrator.
                        </small>
                      )}

                      {/* ✅ Generic athlete-side error only (never “Invalid Event Code”) */}
                      {eventError && <small style={{ color: "red" }}>{eventError}</small>}
                    </div>
                  </div>
                </div>

                {/* Basketball-specific fields */}
                {sport === "Basketball" && (
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <small className="text">Position*</small>
                        <select
                          className="form-control"
                          name="basketball_position"
                          required
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        >
                          <option value="">Select Position</option>
                          <option value="G">G</option>
                          <option value="F">F</option>
                          <option value="C">C</option>
                          <option value="GF">GF</option>
                          <option value="FC">FC</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <small className="text">Dominant Hand*</small>
                        <select
                          className="form-control"
                          name="dominant_hand"
                          required
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        >
                          <option value="">Select</option>
                          <option value="Right">Right</option>
                          <option value="Left">Left</option>
                          <option value="Both">Both</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <small className="text">Wingspan (in)*</small>
                        <input
                          type="number"
                          className="form-control"
                          name="wingspan_in"
                          required
                          placeholder="Enter Wingspan in Inches"
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <small className="text">Vertical Jump (in)*</small>
                        <input
                          type="number"
                          className="form-control"
                          name="vertical_jump_in"
                          required
                          placeholder="Enter Vertical Jump in Inches"
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        />
                      </div>

                      <div className="mb-3">
                        <small className="text">40-Yard Time (sec)</small>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="forty_yard_time_sec"
                          placeholder="Enter 40-Yard Dash Time"
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Baseball Specific Fields */}
                {sport === "Baseball" && (
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <small className="text">Bats*</small>
                        <select
                          className="form-control"
                          name="bats"
                          required
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        >
                          <option value="">Select</option>
                          <option value="R">Right</option>
                          <option value="L">Left</option>
                          <option value="S">Switch</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <small className="text">Throws*</small>
                        <select
                          className="form-control"
                          name="throws"
                          required
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        >
                          <option value="">Select</option>
                          <option value="R">Right</option>
                          <option value="L">Left</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <small className="text d-block mb-2">Positions*</small>
                        <div className="d-flex flex-wrap" style={{ gap: "12px" }}>
                          {["P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"].map((pos) => (
                            <div key={pos} className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`position-${pos}`}
                                name="positions"
                                value={pos}
                                required={false} // allow multiple, validation can be custom
                              />
                              <label className="form-check-label" htmlFor={`position-${pos}`}>
                                {pos}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <small className="text">Exit Velo Max (mph)*</small>
                        <input
                          type="number"
                          className="form-control"
                          name="exit_velo_max_mph"
                          required
                          placeholder="Enter Exit Velocity"
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        />
                      </div>

                      <div className="mb-3">
                        <small className="text">Pop Time (sec)*</small>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="pop_time_sec"
                          required
                          placeholder="Enter Pop Time"
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        />
                      </div>

                      <div className="mb-3">
                        <small className="text">Sixty Yard Time (sec)</small>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="sixty_yard_time_sec"
                          placeholder="Enter Sixty Yard Time"
                          style={{ borderRadius: "10px", padding: "12px 20px" }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Football Specific Fields */}
                {sport === "Football" && (
                  <>
                    <div className="mb-3">
                      <small className="text">Handedness*</small>
                      <select
                        className="form-control"
                        name="handedness"
                        required
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      >
                        <option value="">Select</option>
                        <option value="Right">Right</option>
                        <option value="Left">Left</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <small className="text">40 Yard Time (sec)</small>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="forty_yard_time_sec"
                        placeholder="Enter 40 Yard Time"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Bench Reps @225*</small>
                      <input
                        type="number"
                        className="form-control"
                        name="bench_reps_225"
                        required
                        placeholder="Enter Bench Reps @225"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text">Vertical Jump (in)*</small>
                      <input
                        type="number"
                        className="form-control"
                        name="vertical_jump_in"
                        required
                        placeholder="Enter Vertical Jump in Inches"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>

                    <div className="mb-3">
                      <small className="text d-block mb-2">Positions*</small>
                      <div className="d-flex flex-wrap" style={{ gap: "12px" }}>
                        {["QB", "RB", "WR", "TE", "OL", "DL", "LB", "CB", "S", "K", "P", "KR", "PR"].map((pos) => (
                          <div key={pos} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`position-${pos}`}
                              name="positions"
                              value={pos}
                            />
                            <label className="form-check-label" htmlFor={`position-${pos}`}>
                              {pos}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Hockey Specific Fields */}
                {sport === "Hockey" && (
                  <>
                    <div className="mb-3">
                      <small className="text">Shoots*</small>
                      <select
                        className="form-control"
                        name="shoots"
                        required
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      >
                        <option value="">Select</option>
                        <option value="L">Left</option>
                        <option value="R">Right</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <small className="text">Position*</small>
                      <select
                        className="form-control"
                        name="position"
                        required
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      >
                        <option value="">Select</option>
                        <option value="F">Forward (F)</option>
                        <option value="D">Defense (D)</option>
                        <option value="G">Goalie (G)</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Soccer Specific Fields */}
                {sport === "Soccer" && (
                  <>
                    <div className="mb-3">
                      <small className="text">Dominant Foot*</small>
                      <select
                        className="form-control"
                        name="dominant_foot"
                        required
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      >
                        <option value="">Select</option>
                        <option value="Right">Right</option>
                        <option value="Left">Left</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <small className="text d-block mb-2">Positions*</small>
                      <div className="d-flex flex-wrap" style={{ gap: "12px" }}>
                        {["GK", "DF", "MF", "FW"].map((pos) => (
                          <div key={pos} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`position-${pos}`}
                              name="positions"
                              value={pos}
                            />
                            <label className="form-check-label" htmlFor={`position-${pos}`}>
                              {pos}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-3">
                      <small className="text">40 Meter Time (sec)</small>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="forty_meter_time_sec"
                        placeholder="Enter 40 Meter Time"
                        style={{ borderRadius: "10px", padding: "12px 20px" }}
                      />
                    </div>
                  </>
                )}

                {sport && (
                  <div className="mb-3">
                    <small className="text">Notes</small>
                    <textarea className="form-control" name="notes" placeholder="Additional Notes" style={{ borderRadius: "10px", padding: "12px 20px" }} />
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary mt-3"
                  disabled={noEventsForSport}
                >
                  {activeTab === "certified" ? "Submit Certified Athlete" : "Submit Community Entry"}
                </button>
              </form>
            ) : (
              <div className="mt-4">
                <h5>Complete Payment</h5>
                <Elements stripe={stripePromise}>
                  <PaymentForm
                    onPaymentSuccess={async () => {
                      if (!tempUploadData) {
                        toast.error("No data to submit!");
                        return;
                      }

                      try {
                        const res = await axios.post(API_URL, tempUploadData, {
                          headers: { Authorization: apiKey },
                        });

                        if (res.data?.message === "Duplicate avoided. EventCode updated.") {
                          toast.error("This email has already submitted this form.");
                          return;
                        }

                        toast.success("Certified Athlete submitted successfully!");
                        navigate("/Thankyou", { state: { fromForm: true } });
                      } catch (err) {
                        console.error("Post-payment submission error:", err);
                        toast.error("Failed to submit data after payment!");
                      }
                    }}
                  />
                </Elements>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}